/**
 * Context for managing spaces state across the application
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { Space, SpaceStatus } from '../types';
import { spacesApi, ApiClientError } from '../api/client';

// State interface
interface SpacesState {
  spaces: Space[];
  selectedSpaceId: string | null;
  loading: boolean;
  error: string | null;
  nextCursor?: string;
}

// Action types
type SpacesAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { spaces: Space[]; nextCursor?: string; append?: boolean } }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'SELECT_SPACE'; payload: string | null }
  | { type: 'ADD_SPACE'; payload: Space }
  | { type: 'UPDATE_SPACE'; payload: Space }
  | { type: 'DELETE_SPACE'; payload: string }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: SpacesState = {
  spaces: [],
  selectedSpaceId: null,
  loading: false,
  error: null,
};

// Reducer
function spacesReducer(state: SpacesState, action: SpacesAction): SpacesState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        spaces: action.payload.append
          ? [...state.spaces, ...action.payload.spaces]
          : action.payload.spaces,
        nextCursor: action.payload.nextCursor,
      };

    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };

    case 'SELECT_SPACE':
      return { ...state, selectedSpaceId: action.payload };

    case 'ADD_SPACE':
      return { ...state, spaces: [action.payload, ...state.spaces] };

    case 'UPDATE_SPACE':
      return {
        ...state,
        spaces: state.spaces.map((s) =>
          s.spaceId === action.payload.spaceId ? action.payload : s
        ),
      };

    case 'DELETE_SPACE':
      return {
        ...state,
        spaces: state.spaces.filter((s) => s.spaceId !== action.payload),
        selectedSpaceId:
          state.selectedSpaceId === action.payload ? null : state.selectedSpaceId,
      };

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    default:
      return state;
  }
}

// Context value interface
interface SpacesContextValue {
  state: SpacesState;
  // Actions
  fetchSpaces: (options?: { cursor?: string }) => Promise<void>;
  selectSpace: (spaceId: string | null) => void;
  createSpace: (name: string, visibility?: 'private' | 'internal', templateId?: string) => Promise<Space>;
  updateSpace: (spaceId: string, updates: { name?: string; visibility?: 'private' | 'internal' }) => Promise<Space>;
  deleteSpace: (spaceId: string) => Promise<void>;
  clearError: () => void;
  // Selectors
  getSelectedSpace: () => Space | null;
  getSpaceById: (spaceId: string) => Space | undefined;
  getSpacesByStatus: (status: SpaceStatus) => Space[];
}

// Create context
const SpacesContext = createContext<SpacesContextValue | null>(null);

// Provider component
interface SpacesProviderProps {
  children: ReactNode;
}

export function SpacesProvider({ children }: SpacesProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(spacesReducer, initialState);

  // Fetch spaces
  const fetchSpaces = useCallback(async (options?: { cursor?: string }) => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await spacesApi.list({
        limit: 20,
        cursor: options?.cursor,
      });
      dispatch({
        type: 'FETCH_SUCCESS',
        payload: {
          spaces: response.spaces,
          nextCursor: response.nextCursor,
          append: !!options?.cursor,
        },
      });
    } catch (error) {
      const message =
        error instanceof ApiClientError
          ? error.message
          : 'Failed to fetch spaces';
      dispatch({ type: 'FETCH_ERROR', payload: message });
    }
  }, []);

  // Select a space
  const selectSpace = useCallback((spaceId: string | null) => {
    dispatch({ type: 'SELECT_SPACE', payload: spaceId });
  }, []);

  // Create a new space
  const createSpace = useCallback(
    async (
      name: string,
      visibility: 'private' | 'internal' = 'private',
      templateId?: string
    ) => {
      const space = await spacesApi.create({ name, visibility, templateId });
      dispatch({ type: 'ADD_SPACE', payload: space });
      return space;
    },
    []
  );

  // Update an existing space
  const updateSpace = useCallback(
    async (
      spaceId: string,
      updates: { name?: string; visibility?: 'private' | 'internal' }
    ) => {
      const space = await spacesApi.update(spaceId, updates);
      dispatch({ type: 'UPDATE_SPACE', payload: space });
      return space;
    },
    []
  );

  // Delete a space
  const deleteSpace = useCallback(async (spaceId: string) => {
    await spacesApi.delete(spaceId);
    dispatch({ type: 'DELETE_SPACE', payload: spaceId });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Get selected space
  const getSelectedSpace = useCallback(() => {
    if (!state.selectedSpaceId) return null;
    return state.spaces.find((s) => s.spaceId === state.selectedSpaceId) || null;
  }, [state.selectedSpaceId, state.spaces]);

  // Get space by ID
  const getSpaceById = useCallback(
    (spaceId: string) => {
      return state.spaces.find((s) => s.spaceId === spaceId);
    },
    [state.spaces]
  );

  // Get spaces by status
  const getSpacesByStatus = useCallback(
    (status: SpaceStatus) => {
      return state.spaces.filter((s) => s.status === status);
    },
    [state.spaces]
  );

  // Memoize context value
  const value = useMemo<SpacesContextValue>(
    () => ({
      state,
      fetchSpaces,
      selectSpace,
      createSpace,
      updateSpace,
      deleteSpace,
      clearError,
      getSelectedSpace,
      getSpaceById,
      getSpacesByStatus,
    }),
    [
      state,
      fetchSpaces,
      selectSpace,
      createSpace,
      updateSpace,
      deleteSpace,
      clearError,
      getSelectedSpace,
      getSpaceById,
      getSpacesByStatus,
    ]
  );

  return (
    <SpacesContext.Provider value={value}>{children}</SpacesContext.Provider>
  );
}

// Hook to use the spaces context
export function useSpaces(): SpacesContextValue {
  const context = useContext(SpacesContext);
  if (!context) {
    throw new Error('useSpaces must be used within a SpacesProvider');
  }
  return context;
}

// Export context for testing
export { SpacesContext };
